import React, { Fragment } from 'react'

import numeral from 'numeral'
import TextTruncate from 'react-text-truncate'

import PropTypes from 'prop-types'

import I18n from 'Pods/Shared/I18n'
import Icon from 'Pods/Shared/Icon/Icon'
import Loader from 'Pods/Shared/Loader'
import SharedTable from 'Pods/Shared/Table/SharedTable'
import TableHeaders from 'Pods/Shared/Table/TableHeaders'

import ChildRow from './ChildRow'

const nameColumnIndex = 1
const priceColumnIndex = 3
const KEY_PREFIX = 'profile.my_models.pack_creation'

const translate = (key) => I18n.t(`${KEY_PREFIX}.${key}`, { ns: 'rails' })

const indexIncluded = (included) => new Map(included.map(entry => [`${entry.type}:${entry.id}`, entry]))

const pickIncluded = (includedIndex, relationship, type) => (
  relationship.data.map(({ id }) => includedIndex.get(`${type}:${id}`)).filter(Boolean)
)

class ReviewStep extends React.Component {
  constructor() {
    super()

    this.state = {
      table: {
        className: null,
        select: false,
        headers: [],
        rows: [],
        colgroup: [],
      },
      headers: [
        {
          value: 'thumbnail', title: 'Thumbnail', sortable: false, centered: false,
        },
        {
          value: 'name', title: 'Name', sortable: true, centered: false,
        },
        {
          value: 'score', title: 'Score', sortable: true, centered: false,
        },
        {
          value: 'price', title: 'Price', sortable: true, centered: false,
        },
        {
          value: 'total_files', title: 'Total files', sortable: true, centered: false,
        },
        {
          value: 'formats', title: 'Formats', sortable: true, centered: false,
        },
        {
          value: 'images', title: 'Images', sortable: true, centered: false,
        },
        {
          value: 'size', title: 'Size', sortable: true, centered: false,
        },
        {
          value: 'expand', title: '', sortable: false, centered: false,
        },
      ],
      sortingFilters: { sortBy: ['name', 'asc'] },
      selectedRows: [],
      selectAll: true,
      items: [],
      isDataFetched: false,
      hasError: false,
    }

    this.onSortingChange = this.onSortingChange.bind(this)
    this.onExpandClick = this.onExpandClick.bind(this)
    this.onSelectAll = this.onSelectAll.bind(this)
    this.onCheckboxClick = this.onCheckboxClick.bind(this)
    this.countPrice = this.countPrice.bind(this)
    this.handleRetry = this.handleRetry.bind(this)
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.itemIds !== this.props.itemIds) {
      this.fetchData(nextProps.itemIds)
    }
  }

  componentDidMount() {
    this.fetchData()
  }

  fetchData(itemIds = this.props.itemIds) {
    this.setState({ isDataFetched: false, hasError: false })

    return axios
      .post(
        '/api/internal/items/collection-items',
        { item_ids: itemIds },
        {
          withCredentials: true,
        }
      )
      .then((response) => this.onFetchSuccess(response))
      .catch(() => this.onFetchError())
  }

  onFetchSuccess(response) {
    const included = response.data.included || []

    this.setState({
      items: response.data.data,
      isDataFetched: true,
      hasError: false,
    })

    this.makeTable(response.data.data, included)
  }

  onFetchError() {
    this.setState({ isDataFetched: true, hasError: true })

    App.events.publish('app:notification:flash:error', translate('flash_error'))
  }

  handleRetry() {
    this.fetchData()
  }

  makeTable(items, included) {
    const includedIndex = indexIncluded(included)

    const rows = items.map((row) => {
      return {
        id: row.id,
        child: <ChildRow
          images={pickIncluded(includedIndex, row.relationships.itemImages, 'itemImages')}
          files={pickIncluded(includedIndex, row.relationships.itemFiles, 'itemFiles')}
          colSpan={10}
        />,
        childExpanded: false,
        columns: [{
          content: <img src={row.attributes.primaryImage.thumbUrl} alt={row.attributes.title} />,
          customClass: 'u-text-center',
        }, {
          content: <TextTruncate line={2} truncateText="…" text={row.attributes.title} />,
          customClass: 'product-text__title',
          contentStr: row.attributes.title,
        }, {
          content: numeral(row.attributes.publishingScore).format('0.0'),
          customClass: `text-semibold u-text-center ${row.attributes.publishingScore >= 6 ? 'text-green' : 'text-warning'}`,
          contentStr: row.attributes.publishingScore,
        }, {
          content: `$${row.attributes.price}`,
          customClass: 'text-semibold u-text-center',
          contentStr: row.attributes.price,
        }, {
          content: row.attributes.fileTypesCount,
          customClass: 'u-text-center',
          contentStr: row.attributes.fileTypesCount,
        }, {
          content: row.attributes.formatsCount,
          customClass: 'u-text-center',
          contentStr: row.attributes.formatsCount,
        }, {
          content: row.attributes.itemImagesCount,
          customClass: 'u-text-center',
          contentStr: row.attributes.itemImagesCount,
        }, {
          content: row.attributes.size,
          customClass: 'u-text-center',
          contentStr: row.attributes.size,
        }, {
          content: <Icon type={['fas', 'caret-down']} className="expansion-caret" notSpaced />,
          customClass: 'u-text-center',
        }],
      }
    }).sort((a, b) => {
      return a.columns[nameColumnIndex].contentStr.localeCompare(b.columns[nameColumnIndex].contentStr)
    })

    this.setState({
      table: {
        className: 'table-with-borders table-with-borders--compact products-listing',
        select: true,
        headers: false,
        rows,
        colgroup: ['4%', '8%', '29%', '9%', '9%', '9%', '9%', '9%', '9%', '5%'],
      },
      selectedRows: rows,
      selectAll: true,
    })
  }

  onSortingChange(newFilters) {
    const newState = this.state
    const filterIndex = {
      name: 1,
      score: 2,
      price: 3,
      total_files: 4,
      formats: 5,
      images: 6,
      size: 7,
    }[newFilters.sortBy[0]]

    newState.sortingFilters = newFilters

    newState.table.rows = this.state.table.rows.sort((a, b) => {
      if (newFilters.sortBy[1] === 'asc') {
        if (['score', 'price', 'total_files', 'formats', 'images', 'size'].includes(newFilters.sortBy[0])) {
          return numeral(a.columns[filterIndex].contentStr)._value - numeral(b.columns[filterIndex].contentStr)._value
        }
        return a.columns[filterIndex].contentStr.localeCompare(b.columns[filterIndex].contentStr)
      } if (['score', 'price', 'total_files', 'formats', 'images', 'size'].includes(newFilters.sortBy[0])) {
        return numeral(b.columns[filterIndex].contentStr)._value - numeral(a.columns[filterIndex].contentStr)._value
      }
      return b.columns[filterIndex].contentStr.localeCompare(a.columns[filterIndex].contentStr)
    })

    this.setState(newState)
  }

  onExpandClick(index) {
    const newState = this.state
    const targetedRow = newState.table.rows.find((row) => row.id === index)

    targetedRow.childExpanded = !targetedRow.childExpanded
    this.setState(newState)
  }

  onSelectAll() {
    if (this.state.selectAll) {
      this.setState({
        selectAll: false,
        selectedRows: [],
      })
    } else {
      const list = this.state.selectedRows

      list.length = 0

      this.state.table.rows.map((value) => list.push(value))

      this.setState({
        selectAll: true,
        selectedRows: list,
      })
    }
  }

  onCheckboxClick(id) {
    const model = this.state.selectedRows.filter(item => item.id === id)

    if (model.length > 0) {
      this.setState(prevState => ({
        selectedRows: prevState.selectedRows.filter(item => item.id !== id),
        selectAll: false,
      }))
    } else {
      const model = this.state.table.rows.filter(item => item.id === id)
      const list = this.state.selectedRows

      list.push(model[0])

      this.setState({
        selectedRows: list,
        selectAll: list.length === this.state.table.rows.length,
      })
    }
  }

  countPrice() {
    let price

    if (this.state.selectedRows.length === 1) {
      price = this.state.selectedRows[0].columns[priceColumnIndex].contentStr
    } else {
      price = this.state.selectedRows.map((row) => row.columns[priceColumnIndex].contentStr).reduce((a, b) => { return a + b; }, 0)
    }

    return numeral(price).format('$0.00')
  }

  renderContent() {
    if (this.state.hasError) {
      return (
        <div className="empty-content u-text-center" role="alert">
          <p className="text-default">{translate('load_error')}</p>
          <button className="btn btn-primary" type="button" onClick={this.handleRetry}>
            {translate('retry')}
          </button>
        </div>
      )
    }

    if (!this.state.isDataFetched) {
      return (
        <div className="empty-content u-text-center">
          <Loader isFullHeight />
        </div>
      )
    }

    return (
      <Fragment>
        <div className="overlay-modal__content">
          <SharedTable
            table={this.state.table}
            onRowClick={this.onExpandClick}
            onCheckboxClick={this.onCheckboxClick}
            selectedRows={this.state.selectedRows}
          />
        </div>
        <div className="overlay-modal__footer">
          <div className="footer__info u-text-left">
            <span className="text-dark">
              <Icon type={['far', 'file']} />
              {this.state.selectedRows.length} models selected.
              Total price <span className="text-semibold text-green">
                {this.state.selectedRows.length > 0 ? this.countPrice() : '$0'}
              </span>
            </span>
          </div>
          <div className="footer__action u-text-right">
            <button
              className="btn btn-primary"
              type="button"
              disabled={this.state.selectedRows.length < 2}
              onClick={() => this.props.changeStep('loading', this.state.selectedRows.map(row => row.id), null, null, null)}
            >Next
            </button>
          </div>
        </div>
      </Fragment>
    )
  }

  render() {
    return (
      <div className="overlay-modal--folders-structure-review">
        <header>
          <button className="btn btn-transparent overlay-modal--close-inner" onClick={this.props.onModalClose}>
            <Icon type={['fal', 'times']} color="grey-dark" />
          </button>
          <div className="header-text">
            <div className="section__title">Create a pack</div>
            <p className="text-default">
              You are about to create a pack of your 3D assets. Please review the products you want to include in the pack below.
            </p>
          </div>
          <table width="100%" className="table-with-borders">
            <colgroup>
              {this.state.table.colgroup.map((col, id) => <col key={id+col} style={{ width: col }} />)}
            </colgroup>
            <TableHeaders
              headers={this.state.headers}
              sortingChange={this.onSortingChange}
              sortingFilters={this.state.sortingFilters}
              selectAll={this.onSelectAll}
              isSelected={this.state.selectAll}
            />
          </table>
        </header>

        {this.renderContent()}
      </div>
    )
  }
}

ReviewStep.propTypes = {
  onModalClose: PropTypes.func.isRequired,
  changeStep: PropTypes.func.isRequired,
  itemIds: PropTypes.array.isRequired,
}

export default ReviewStep
